import React, { useCallback, useEffect, useState } from 'react';
import { authFetch, API } from '../api';

const R = `${API}/returns`;

/**
 * Statistics → Unclaimed COD — the analysis, separate from the to-do.
 *
 * Replaces three hand-kept spreadsheets, including 727 imported historic cases
 * back to 2024. The two questions it exists to answer are whether a country is
 * still worth offering cash on delivery in, and who keeps doing this — neither
 * of which was answerable while the data lived in yearly CSVs.
 */

type Field = { key: string; label: string; type: string };
type View = 'cases' | 'country' | 'customer' | 'month';

const money = (v: any) =>
  v === null || v === undefined || v === '' ? '—' : `€${Number(v).toFixed(2)}`;

export default function UnclaimedCodStats() {
  const [view, setView] = useState<View>('country');
  const [fields, setFields] = useState<Field[]>([]);
  const [rows, setRows] = useState<any[]>([]);
  const [report, setReport] = useState<any[]>([]);
  const [totalEur, setTotalEur] = useState(0);
  const [country, setCountry] = useState('');
  const [customer, setCustomer] = useState('');
  const [loading, setLoading] = useState(true);

  const loadCases = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (country) qs.set('country', country);
    if (customer) qs.set('customer', customer);
    authFetch(`${R}/unclaimed-cod/cases?${qs}`)
      .then(r => r.json())
      .then(d => { setFields(d.fields || []); setRows(d.rows || []); setTotalEur(d.total_eur || 0); })
      .finally(() => setLoading(false));
  }, [country, customer]);

  useEffect(() => {
    if (view === 'cases') { loadCases(); return; }
    setLoading(true);
    authFetch(`${R}/unclaimed-cod/report?group_by=${view}`)
      .then(r => r.json()).then(d => setReport(d.rows || []))
      .finally(() => setLoading(false));
  }, [view, loadCases]);

  const TABS: [View, string][] = [
    ['country', 'By country'], ['customer', 'Repeat customers'],
    ['month', 'By month'], ['cases', 'All cases'],
  ];

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setView(k)}
            className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
              view === k ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>
            {label}
          </button>
        ))}
        <div className="flex-1" />
        <a href={`${R}/unclaimed-cod/export`}
           className="px-3 py-1.5 rounded-lg text-sm bg-gray-800 text-gray-300 hover:text-white">
          Export CSV
        </a>
      </div>

      {loading && <div className="text-gray-500 text-sm py-6">Loading…</div>}

      {!loading && view !== 'cases' && (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-800">
            <table className="w-full text-sm">
              <thead className="text-gray-500 text-xs bg-gray-900/60">
                <tr>
                  <th className="text-left py-2 px-3">
                    {view === 'country' ? 'Country' : view === 'customer' ? 'Customer' : 'Month'}
                  </th>
                  <th className="text-right px-3">Parcels</th>
                  <th className="text-right px-3">Value never collected</th>
                  {view === 'country' && <th className="text-right px-3">Customers</th>}
                  <th className="text-right px-3">First</th>
                  <th className="text-right px-3">Last</th>
                </tr>
              </thead>
              <tbody>
                {report.map((r, i) => (
                  <tr key={i} className="border-t border-gray-800/70">
                    <td className="py-1.5 px-3 text-gray-200">
                      {view === 'customer' ? (r.sample_name || r.key) : (r.key || '??')}
                    </td>
                    <td className={`text-right px-3 tabular-nums ${
                      view === 'customer' && r.cases >= 4 ? 'text-red-400 font-medium'
                        : 'text-gray-300'}`}>{r.cases}</td>
                    <td className="text-right px-3 tabular-nums text-gray-300">{money(r.total_eur)}</td>
                    {view === 'country' && (
                      <td className="text-right px-3 tabular-nums text-gray-500">{r.customers}</td>
                    )}
                    <td className="text-right px-3 text-xs text-gray-600">{r.first_seen}</td>
                    <td className="text-right px-3 text-xs text-gray-600">{r.last_seen}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-600 mt-3 max-w-3xl">
            {view === 'country'
              ? 'Whether a market is still worth offering cash on delivery in. Watch the first-seen date as well as the total — a country that only started recently and is already high is losing money faster than an older one with a bigger number.'
              : view === 'customer'
              ? 'Four or more unclaimed parcels is highlighted. Historic rows are matched on customer name only, so someone who ordered under two spellings may be undercounted.'
              : 'Volume over time, including the imported history back to February 2024.'}
          </p>
        </>
      )}

      {!loading && view === 'cases' && (
        <>
          <div className="flex flex-wrap gap-2 mb-3 text-sm">
            <input value={country} onChange={e => setCountry(e.target.value.toUpperCase())}
                   placeholder="Country" maxLength={2}
                   className="bg-gray-900 border border-gray-700 rounded px-2 py-1.5 w-28" />
            <input value={customer} onChange={e => setCustomer(e.target.value)}
                   placeholder="Customer"
                   className="bg-gray-900 border border-gray-700 rounded px-2 py-1.5" />
            <span className="text-gray-500 self-center">
              {rows.length} cases · {money(totalEur)}
            </span>
          </div>
          <div className="overflow-x-auto rounded-lg border border-gray-800">
            <table className="w-full text-sm">
              <thead className="text-gray-500 text-xs bg-gray-900/60">
                <tr>{fields.map(f => (
                  <th key={f.key} className={`py-2 px-3 ${
                    f.type === 'money' || f.type === 'number' ? 'text-right' : 'text-left'}`}>
                    {f.label}
                  </th>))}
                </tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className="border-t border-gray-800/70">
                    {fields.map(f => (
                      <td key={f.key} className={`py-1.5 px-3 ${
                        f.type === 'money' || f.type === 'number'
                          ? 'text-right tabular-nums text-gray-300' : 'text-gray-400'}`}>
                        {f.type === 'money' ? money(r[f.key]) : (r[f.key] ?? '—')}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
